import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { useSnackbar, FormControlLabel, Stack, Switch, Typography } from '@emburse/embark-core';
import { HelpIcon } from '@emburse/embark-icons';
import { $t } from '../../hooks/useTranslation';
import IconPopover from '../../components/IconPopover';
import { toggleAuditAutoInquire, toggleAuditInquiryEditable } from '../../api/policy';
function CorpAuditConfigInquirySettings({ pageloadData }) {
    const { enqueueSnackbar } = useSnackbar();
    const [enableAutoInquiryMode, setEnableAutoInquiryMode] = useState(false);
    const [allowExpenseEdit, setAllowExpenseEdit] = useState(false);
    const [isTogglingExpenseEdit, setIsTogglingExpenseEdit] = useState(false);
    const [isTogglingAutoInquiry, setIsTogglingAutoInquiry] = useState(false);
    useMemo(() => {
        if (pageloadData) {
            setEnableAutoInquiryMode(pageloadData.BooAuditAutoInquire);
            setAllowExpenseEdit(pageloadData.BooAuditInquiryEditable);
        }
    }, [pageloadData]);
    const isLoading = !pageloadData;
    const toggleErrorText = $t('Failed to toggle inquiry setting, please try again or contact support.');
    return (_jsxs(Stack, { alignItems: "flex-start", spacing: 2, children: [_jsx(Typography, { variant: "h5", children: $t('Inquiry Settings') }), _jsx(Typography, { children: $t('Choose whether InstantAudit should automatically generate inquiries for expenses in violation of your detections rules.') }), _jsx(FormControlLabel, { control: _jsx(Switch, { color: "primary", checked: enableAutoInquiryMode, onChange: async () => {
                        try {
                            if (isTogglingExpenseEdit)
                                return;
                            setIsTogglingAutoInquiry(true);
                            setEnableAutoInquiryMode(!enableAutoInquiryMode);
                            await toggleAuditAutoInquire(!enableAutoInquiryMode);
                            enqueueSnackbar($t('Preference saved'), { variant: 'success' });
                        }
                        catch (error) {
                            enqueueSnackbar(toggleErrorText, { variant: 'error' });
                            setEnableAutoInquiryMode(!enableAutoInquiryMode);
                        }
                        finally {
                            setIsTogglingAutoInquiry(false);
                        }
                    }, "data-qa": "toggle-auto-inquiry-mode" }), label: _jsxs(Stack, { direction: "row", children: [_jsx(Typography, { children: $t('Enable Auto-Inquiry Mode') }), _jsx(IconPopover, { title: $t('Auto-Inquiry Mode'), content: $t('When Auto-Inquiry mode is enabled, an inquiry is automatically sent to the submitter for each of their ' +
                                "submitted expenses that are flagged by your company's audit rules."), iconUrl: HelpIcon })] }), disabled: isLoading || isTogglingAutoInquiry }), _jsx(FormControlLabel, { control: _jsx(Switch, { color: "primary", checked: allowExpenseEdit, onChange: async () => {
                        try {
                            if (isTogglingAutoInquiry || isTogglingExpenseEdit)
                                return;
                            setAllowExpenseEdit(!allowExpenseEdit);
                            setIsTogglingExpenseEdit(true);
                            await toggleAuditInquiryEditable(!allowExpenseEdit);
                            enqueueSnackbar($t('Preference saved'), { variant: 'success' });
                        }
                        catch (error) {
                            enqueueSnackbar(toggleErrorText, { variant: 'error' });
                            setAllowExpenseEdit(!allowExpenseEdit);
                        }
                        finally {
                            setIsTogglingExpenseEdit(false);
                        }
                    }, "data-qa": "toggle-expense-edit" }), label: _jsxs(Stack, { direction: "row", children: [_jsx(Typography, { children: $t('Allow expense edit for automatically created inquiries') }), _jsx(IconPopover, { title: $t('Editing Auto-Inquiries'), content: $t('Allow submitters to edit expense details withing automatically created inquiries. ' +
                                'If this option is disabled, submitters may only enter a simple test response to the inquiry.'), iconUrl: HelpIcon })] }), disabled: isLoading ||
                    isTogglingExpenseEdit ||
                    isTogglingAutoInquiry ||
                    !enableAutoInquiryMode })] }));
}
export default CorpAuditConfigInquirySettings;
